/**
 * BreastPreventionProgram.jsx — iter653
 * Section 5d "🎯 12-Month Personalized Breast Prevention Program" (BPP).
 *
 * Educational. NON-diagnostic. Lexicon enforced server-side. Soft verbs only.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHealthEvent } from "@/utils/healthEvents";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Target, Sparkles, RefreshCw, Loader2, ShieldAlert, ShieldCheck, Calendar,
  TrendingUp, ListChecks, Award, ScrollText, Apple, Activity, Moon,
  HeartPulse, Pill, Scale, WineOff, Cigarette, FlaskConical, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import MethodologyInfo from "@/components/MethodologyInfo";  // iter701 — methodology popup

const TX = {
  bg: {
    title: "🎯 12-месечна персонализирана профилактика на гръдното здраве",
    subtitle:
      "Дългосрочна образователна програма за оптимизация на тъканната среда чрез които може да променим lifestyle интервенции. НЕ е диагностично или лечебно средство.",
    refresh: "Обнови програмата",
    enrich: "✨ AI наратив",
    enriching: "Генерира…",
    cached: "от кеш",
    forceRefresh: "Нов AI анализ",
    timeoutMsg: "AI отговорът отнема повече от обичайното. Опитайте отново след минута.",
    noAi: "Натиснете бутона за AI наратив, за да получите кратък персонализиран преглед.",
    empty: "Зареждане…",
    part1: "Част 1 · Приоритети за профилактика (топ 5)",
    part2: "Част 2 · 90-дневен план за действие",
    part3: "Част 3 · Цели за тъканната среда (lifestyle)",
    part4: "Част 4 · Цели за гръдната тъкан",
    part5: "Част 5 · Специфична профилактика за наличните състояния",
    part6: "Част 6 · График за проследяване",
    part7: "Част 7 · Прогноза за прогрес",
    final: "Вашата персонализирана пътна карта",
    why: "Защо има значение",
    difficulty: "Сложност",
    effect: "Очакван ефект",
    impactLabel: "ефект",
    burdenLabel: "натоварване",
    reassess: "Реасесмент",
    focus: "Фокус",
    note: "Бележка",
    keyDrivers: "Ключови биологични двигатели",
    currentState: "Сегашно състояние на тъканта",
    ninetyDayFocus: "Фокус за първите 90 дни",
    twelveMonth: "12-месечна стратегия",
    successMetrics: "Метрики за успех",
    visits: "Визити за проследяване",
    part5EmptyTitle: "Все още няма установени специфични състояния",
    part5EmptyBody:  "Тази секция показва индивидуален план за всяко конкретно състояние, което сте въвели в анамнезата си (напр. фиброкистозна болест, ALH, ADH). За да я попълните, добавете състояния в Профил → Анамнеза.",
  },
  en: {
    title: "🎯 12-Month Personalized Breast Prevention Program",
    subtitle:
      "Long-term educational program optimising the tissue environment through modifiable lifestyle interventions. NOT a diagnostic or therapeutic device.",
    refresh: "Refresh program",
    enrich: "✨ AI narrative",
    enriching: "Generating…",
    cached: "cached",
    forceRefresh: "New AI analysis",
    timeoutMsg: "AI is taking longer than usual. Try again in a minute.",
    noAi: "Click the AI narrative button for a short personalised summary.",
    empty: "Loading…",
    part1: "Part 1 · Prevention Priorities (top 5)",
    part2: "Part 2 · 90-Day Action Plan",
    part3: "Part 3 · Tissue-Environment Targets (lifestyle)",
    part4: "Part 4 · Breast-Specific Goals",
    part5: "Part 5 · Condition-Specific Prevention",
    part6: "Part 6 · Follow-Up Schedule",
    part7: "Part 7 · Progress Projection",
    final: "Your Personalised Roadmap",
    why: "Why it matters",
    difficulty: "Difficulty",
    effect: "Expected effect",
    impactLabel: "impact",
    burdenLabel: "burden",
    reassess: "Reassess",
    focus: "Focus",
    note: "Note",
    keyDrivers: "Key biological drivers",
    currentState: "Current tissue state",
    ninetyDayFocus: "First-90-day focus",
    twelveMonth: "12-month strategy",
    successMetrics: "Success metrics",
    visits: "Follow-up visits",
    part5EmptyTitle: "No specific diagnoses on file yet",
    part5EmptyBody:  "This section shows an individual plan for each specific condition recorded in your history (e.g. fibrocystic disease, ALH, ADH). Add conditions in Profile → Medical history to populate it.",
  },
  es: {
    title: "🎯 Programa Personalizado de 12 Meses para Prevención Mamaria",
    subtitle:
      "Programa educativo a largo plazo para optimizar el entorno tisular mediante intervenciones modificables de estilo de vida. NO es un dispositivo diagnóstico o terapéutico.",
    refresh: "Actualizar programa",
    enrich: "✨ Narrativa IA",
    enriching: "Generando…",
    cached: "en caché",
    forceRefresh: "Nuevo análisis IA",
    timeoutMsg: "La IA está tardando más de lo habitual. Inténtelo en un minuto.",
    noAi: "Pulse el botón Narrativa IA para un resumen personalizado corto.",
    empty: "Cargando…",
    part1: "Parte 1 · Prioridades de prevención (top 5)",
    part2: "Parte 2 · Plan de acción de 90 días",
    part3: "Parte 3 · Objetivos del entorno tisular (lifestyle)",
    part4: "Parte 4 · Objetivos específicos del seno",
    part5: "Parte 5 · Prevención específica para condiciones presentes",
    part6: "Parte 6 · Calendario de seguimiento",
    part7: "Parte 7 · Proyección de progreso",
    final: "Su hoja de ruta personalizada",
    why: "Por qué importa",
    difficulty: "Dificultad",
    effect: "Efecto esperado",
    impactLabel: "impacto",
    burdenLabel: "carga",
    reassess: "Reevaluar",
    focus: "Enfoque",
    note: "Nota",
    keyDrivers: "Conductores biológicos clave",
    currentState: "Estado actual del tejido",
    ninetyDayFocus: "Enfoque primeros 90 días",
    twelveMonth: "Estrategia 12 meses",
    successMetrics: "Métricas de éxito",
    visits: "Visitas de seguimiento",
    part5EmptyTitle: "Aún no hay diagnósticos específicos registrados",
    part5EmptyBody:  "Esta sección muestra un plan individual para cada condición concreta registrada en su historial (p. ej. enfermedad fibroquística, ALH, ADH). Añada condiciones en Perfil → Historia médica para completarla.",
  },
};

const CAT_ICON = {
  Nutrition: Apple, "Physical Activity": Activity, Sleep: Moon, Stress: HeartPulse,
  Weight: Scale, Alcohol: WineOff, Smoking: Cigarette, Supplements: Pill,
};

const CAT_LABEL = {
  bg: {
    Nutrition: "Хранене", "Physical Activity": "Физическа активност", Sleep: "Сън",
    Stress: "Стрес", Weight: "Тегло", Alcohol: "Алкохол", Smoking: "Тютюнопушене",
    Supplements: "Добавки",
  },
  en: {
    Nutrition: "Nutrition", "Physical Activity": "Physical Activity", Sleep: "Sleep",
    Stress: "Stress", Weight: "Weight", Alcohol: "Alcohol", Smoking: "Smoking",
    Supplements: "Supplements",
  },
  es: {
    Nutrition: "Nutrición", "Physical Activity": "Actividad física", Sleep: "Sueño",
    Stress: "Estrés", Weight: "Peso", Alcohol: "Alcohol", Smoking: "Tabaquismo",
    Supplements: "Suplementos",
  },
};

const IMPACT_BAR = {
  high:     "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-300/40",
  moderate: "bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-300/40",
  mild:     "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-300/40",
  low:      "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-300/40",
};

export default function BreastPreventionProgram({ snapshot, conditions, lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const catLabels = CAT_LABEL[lang] || CAT_LABEL.bg;

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [enrich, setEnrich] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);

  const computeProgram = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const body = { lang, conditions: conditions || [] };
      if (snapshot) body.bhi_snapshot = snapshot;
      const { data } = await axios.post(`${API}/bhi/prevention-program/preview`, body);
      setProgram(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, snapshot, conditions, lang]);

  useEffect(() => { computeProgram(); }, [computeProgram]);
  // iter810 — Live re-compute on diagnosis add/remove (BPP part5 depends on it).
  useHealthEvent("conditions-changed", () => { computeProgram(true); });

  const runEnrich = async (force = false) => {
    if (!program) return;
    setEnrichLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/bhi/prevention-program/enrich`,
        { program, lang, force_refresh: force },
        { timeout: 90000 },
      );
      setEnrich(data);
    } catch (e) {
      const status = e?.response?.status;
      const isTimeout = status === 502 || status === 504 || e?.code === "ECONNABORTED";
      setEnrich({
        error: isTimeout ? t.timeoutMsg : (e?.response?.data?.detail || e.message),
      });
    } finally {
      setEnrichLoading(false);
    }
  };

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  return (
    <div className="space-y-5" data-testid="bpp-program">
      {/* Header */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-extrabold bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent">
                {t.title}
              </h3>
              {program?.methodology && (
                <MethodologyInfo
                  methodology={program.methodology}
                  limitations={program.limitations}
                  tonePolicy={program.tone_policy}
                  lang={lang}
                  isDark={isDark}
                  testid="bpp-prevention"
                />
              )}
            </div>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={computeProgram}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="bpp-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t.refresh}
          </button>
          <button
            onClick={() => runEnrich(false)}
            disabled={enrichLoading || !program}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="bpp-enrich-btn"
          >
            {enrichLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {enrichLoading ? t.enriching : t.enrich}
          </button>
        </div>
      </div>

      {program?.disclaimer && (
        <div
          className={`p-3 rounded-xl border-l-4 flex items-start gap-2 ${
            isDark ? "bg-amber-500/10 border-amber-500" : "bg-amber-50 border-amber-500"
          }`}
          data-testid="bpp-disclaimer"
        >
          <ShieldAlert className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
          <p className={`text-[11px] leading-relaxed ${isDark ? "text-amber-200" : "text-amber-700"}`}>
            {program.disclaimer}
          </p>
        </div>
      )}

      {loading && (
        <div className="text-center py-10">
          <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" />
          <p className="mt-2 text-xs opacity-70">{t.empty}</p>
        </div>
      )}
      {err && <div className="p-3 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="bpp-error">{err}</div>}

      {program && !loading && (
        <>
          {/* PART 1 — Priorities */}
          <Block title={t.part1} cardCls={cardCls} testid="bpp-part1">
            <ol className="space-y-2">
              {(program.part1_prevention_priority || []).map((p) => (
                <motion.li
                  key={p.priority}
                  layout
                  className={`p-3 rounded-xl border-l-4 ${IMPACT_BAR[p.impact_key] || IMPACT_BAR.low}`}
                  data-testid={`bpp-priority-${p.target_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm">
                      <span className="opacity-50 mr-1.5">#{p.priority}</span>
                      {p.target}
                    </span>
                    <span className="text-[10px] font-bold opacity-80">{p.impact} {t.impactLabel}</span>
                  </div>
                  <div className="text-[10px] opacity-60 mt-1">{t.burdenLabel}: {p.burden}</div>
                </motion.li>
              ))}
              {(program.part1_prevention_priority || []).length === 0 && (
                <li className="text-sm opacity-60 italic">—</li>
              )}
            </ol>
          </Block>

          {/* PART 2 — 90-day plan */}
          <Block title={t.part2} cardCls={cardCls} testid="bpp-part2">
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(program.part2_90day_action_plan || {}).map(([cat, items]) => {
                const Icon = CAT_ICON[cat] || ListChecks;
                return (
                  <div
                    key={cat}
                    className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`}
                    data-testid={`bpp-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-blue-500" />
                      <span className="font-semibold text-sm">{catLabels[cat] || cat}</span>
                    </div>
                    <div className="space-y-2">
                      {items.map((a, i) => (
                        <div key={i} className="text-[12px] space-y-0.5">
                          <div className="font-semibold flex items-start gap-1.5">
                            <span className="text-blue-500">▸</span>
                            <span>{a.action}</span>
                          </div>
                          {a.why_it_matters && (
                            <div className="opacity-70 pl-3">
                              <span className="font-semibold">{t.why}:</span> {a.why_it_matters}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 pl-3 text-[10px]">
                            {a.difficulty && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700">
                                {t.difficulty}: {a.difficulty}
                              </span>
                            )}
                            {a.expected_biological_effect && (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                                ✓ {a.expected_biological_effect}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* iter917/918 — Evidence-backed nutrition & supplements
                surfaced from CTD + Phenol-Explorer + curated PMIDs.
                Additive to Part 2's editorial action plan; hidden
                when the evidence pipeline has no data. */}
            {program.evidence_meta?.evidence_based_count > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="bpp-part2-evidence">
                <div className={`mb-3 px-3 py-2 rounded-lg border text-[11px] flex items-start gap-2 ${
                  isDark ? "bg-emerald-950/40 border-emerald-800 text-emerald-200"
                         : "bg-emerald-50 border-emerald-200 text-emerald-800"
                }`}>
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-bold">
                      {program.evidence_meta.evidence_based_count} evidence-based препоръки
                    </div>
                    <div className="opacity-80">
                      От: {(program.evidence_meta.evidence_sources || []).join(" · ")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token") || sessionStorage.getItem("nutramilo_access_token") || "";
                        const res = await axios.post(
                          `${API}/bhi/evidence/pdf`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
                        );
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `nutramilo-evidence-${new Date().toISOString().slice(0,10)}.pdf`;
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      } catch { /* silent */ }
                    }}
                    data-testid="bpp-evidence-pdf-btn"
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md ${
                      isDark ? "bg-emerald-800 hover:bg-emerald-700 text-emerald-100"
                             : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }`}
                    title="Изтегли PDF за лекар — clinician-ready one-pager"
                  >
                    <ScrollText className="w-3 h-3" />
                    PDF за лекар
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {["Nutrition", "Supplements"].map((dim) => {
                    const items = (program.evidence_backed_actions || {})[dim] || [];
                    if (items.length === 0) return null;
                    return (
                      <div
                        key={dim}
                        className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`}
                        data-testid={`bpp-evidence-${dim.toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <FlaskConical className="w-4 h-4 text-emerald-500" />
                          <span className="font-semibold text-sm">
                            {dim === "Nutrition" ? "Хранене" : "Добавки"}
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {items.map((it, i) => (
                            <BppEvidenceItem key={i} item={it} isDark={isDark} />
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Block>

          {/* PART 3 — Lifestyle targets */}
          <Block title={t.part3} cardCls={cardCls} testid="bpp-part3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(program.part3_breast_tissue_targets || {}).map(([k, v]) => (
                <div key={k} className={`p-2.5 rounded-lg border ${isDark ? "border-white/10" : "border-gray-100"} text-center`}>
                  <div className="text-[10px] uppercase opacity-60 leading-tight">{k.replace(/_/g, " ")}</div>
                  <div className="text-xs font-bold mt-0.5">{v}</div>
                </div>
              ))}
            </div>
          </Block>

          {/* PART 4 — Breast goals (soft verbs) */}
          <Block title={t.part4} cardCls={cardCls} testid="bpp-part4">
            <ul className="space-y-1">
              {Object.entries(program.part4_breast_specific_goals || {}).map(([k, v]) => (
                <li key={k} className="text-xs flex items-start gap-2">
                  <Award className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
                  <span><span className="font-semibold capitalize">{k.replace(/_/g, " ")}:</span> {v}</span>
                </li>
              ))}
            </ul>
          </Block>

          {/* PART 5 — Condition-specific (always rendered with an explicit empty
              state so the numbering Part 4 → Part 5 → Part 6 is never broken). */}
          <Block title={t.part5} cardCls={cardCls} testid="bpp-part5">
            {(program.part5_condition_specific || []).length > 0 ? (
              <div className="grid md:grid-cols-2 gap-3">
                {program.part5_condition_specific.map((cp) => (
                  <div
                    key={cp.condition_id}
                    className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`}
                    data-testid={`bpp-cond-${cp.condition_id}`}
                  >
                    <h5 className="font-bold text-sm text-indigo-600 dark:text-indigo-400">{cp.title}</h5>
                    {cp.focus && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {cp.focus.map((f, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                    <ul className="mt-2 space-y-0.5 text-[12px]">
                      {(cp.actions || []).map((a, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-indigo-500">▸</span><span>{a}</span>
                        </li>
                      ))}
                    </ul>
                    {cp.note && (
                      <p className="text-[10px] italic opacity-60 mt-2 pt-2 border-t border-black/5 dark:border-white/5">
                        {t.note}: {cp.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`p-4 rounded-xl border border-dashed text-xs ${isDark ? "border-white/15 text-gray-300" : "border-gray-300 text-gray-600"}`}
                data-testid="bpp-part5-empty"
              >
                <div className="font-semibold mb-1">{t.part5EmptyTitle}</div>
                <p className="leading-relaxed opacity-80">{t.part5EmptyBody}</p>
              </div>
            )}
          </Block>

          {/* PART 6 — Follow-up schedule */}
          <Block title={t.part6} cardCls={cardCls} testid="bpp-part6">
            <div className="grid md:grid-cols-3 gap-3">
              {(program.part6_followup_schedule || []).map((v) => (
                <div
                  key={v.id}
                  className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`}
                  data-testid={`bpp-visit-${v.id}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span className="font-semibold text-sm">{v.title}</span>
                  </div>
                  <ul className="space-y-0.5 text-[11px] opacity-80">
                    {(v.reassess || []).map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5"><span>•</span><span>{r}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Block>

          {/* PART 7 — Progress projection */}
          <Block title={t.part7} cardCls={cardCls} testid="bpp-part7">
            <p className={`text-xs italic mb-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {program.part7_progress_projection?.headline}
            </p>
            <ul className="grid sm:grid-cols-2 gap-1">
              {(program.part7_progress_projection?.domains || []).map((d, i) => (
                <li key={i} className="text-xs flex items-start gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </Block>

          {/* FINAL — Roadmap */}
          {program.final_roadmap && (
            <div className={`${cardCls} p-5 bg-gradient-to-br from-blue-500/5 to-indigo-500/5`} data-testid="bpp-final">
              <div className="flex items-center gap-2 mb-3">
                <ScrollText className="w-5 h-5 text-indigo-500" />
                <h4 className="text-base font-extrabold">{t.final}</h4>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <RoadmapBlock label={t.currentState} items={program.final_roadmap.current_tissue_state} />
                <RoadmapBlock label={t.keyDrivers}   items={program.final_roadmap.key_biological_drivers} />
                <RoadmapBlock label={t.ninetyDayFocus} items={program.final_roadmap.ninety_day_focus} accent="blue" />
                <RoadmapBlock
                  label={t.twelveMonth}
                  items={(program.final_roadmap.twelve_month_strategy?.follow_up_visits) || []}
                  accent="indigo"
                />
              </div>
              {(program.final_roadmap.success_metrics || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase font-bold opacity-50 mb-1">{t.successMetrics}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    {program.final_roadmap.success_metrics.slice(0, 8).map((m) => (
                      <div key={m.key} className={`p-2 rounded-lg border text-center text-[11px] ${isDark ? "border-white/10" : "border-gray-100"}`}>
                        <div className="opacity-60 leading-tight">{m.label}</div>
                        <div className="font-bold mt-0.5">{m.current != null ? Math.round(m.current) : "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI narrative */}
          <div className={`${cardCls} p-5`} data-testid="bpp-ai-section">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              <h4 className="text-base font-bold">{t.enrich.replace("✨ ", "")}</h4>
              {enrich?.cached && (
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {t.cached}
                </span>
              )}
            </div>
            {enrich?.narrative ? (
              <div className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-800"}`} data-testid="bpp-ai-text">
                {enrich.narrative}
              </div>
            ) : enrich?.error ? (
              <div className="text-sm text-red-500" data-testid="bpp-ai-error">{enrich.error}</div>
            ) : (
              <div className="text-sm opacity-60 italic">{t.noAi}</div>
            )}
            {enrich?.narrative && (
              <button
                onClick={() => runEnrich(true)}
                disabled={enrichLoading}
                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1.5"
                data-testid="bpp-ai-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${enrichLoading ? "animate-spin" : ""}`} /> {t.forceRefresh}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Block({ title, children, cardCls, testid }) {
  return (
    <div className={`${cardCls} p-5`} data-testid={testid}>
      <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{title}</h4>
      {children}
    </div>
  );
}

function RoadmapBlock({ label, items, accent = "slate" }) {
  const cls = {
    slate:  "text-slate-600 dark:text-slate-300",
    blue:   "text-blue-600 dark:text-blue-300",
    indigo: "text-indigo-600 dark:text-indigo-300",
  }[accent];
  return (
    <div>
      <div className={`text-[10px] uppercase font-bold opacity-60 mb-1 ${cls}`}>{label}</div>
      {(items || []).length === 0 ? (
        <div className="text-xs opacity-50 italic">—</div>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-indigo-500">▸</span>
              <span>{typeof it === "string" ? it : JSON.stringify(it)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// iter917/918 — Compact evidence-backed action row for BPP part 2.
// Shows compound name + inline evidence pill; expanded view has
// pathway + p-value strength + external-source chips (kegg / pubchem
// / pubmed / phenol-explorer / ctd).
function BppEvidenceItem({ item, isDark }) {
  const [open, setOpen] = React.useState(false);
  if (typeof item === "string") {
    return (
      <li className="text-[12px] flex items-start gap-1.5">
        <span className="text-emerald-500">▸</span>
        <span>{item}</span>
      </li>
    );
  }
  const ev = item.evidence || {};
  const kegg = (ev.pathway_kegg || "").replace("KEGG:", "");
  const srcs = ev.sources || [];
  return (
    <li className="text-[12px]" data-testid="bpp-evidence-item">
      <div className="flex items-start gap-1.5">
        <span className="text-emerald-500 mt-0.5">▸</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="font-medium">{item.text}</span>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="bpp-evidence-toggle"
              className={`shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                isDark ? "bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60"
                       : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              }`}
              title="Виж източниците"
            >
              <ShieldCheck className="w-2.5 h-2.5" />
              evidence
            </button>
          </div>
          {open && (
            <div
              data-testid="bpp-evidence-panel"
              className={`mt-1 p-2 rounded-lg text-[10px] ${
                isDark ? "bg-slate-900/60 border border-slate-800"
                       : "bg-slate-50 border border-slate-200"
              }`}
            >
              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                {kegg && (
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    <b>Pathway:</b> {kegg}
                    {ev.pathway_name && ` (${ev.pathway_name})`}
                  </span>
                )}
                {ev.p_value_label && (
                  <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>
                    <b>Доказателство:</b> {ev.p_value_label}
                  </span>
                )}
                {ev.driver_id && (
                  <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                    <b>Драйвер:</b> {ev.driver_id}
                  </span>
                )}
              </div>
              {srcs.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {srcs.map((s, i) => (
                    <a
                      key={i}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`bpp-source-${s.kind}`}
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                        isDark ? "bg-indigo-900/40 hover:bg-indigo-900/60 text-indigo-200"
                               : "bg-indigo-100 hover:bg-indigo-200 text-indigo-700"
                      }`}
                    >
                      {s.label}
                      <ExternalLink className="w-2 h-2 opacity-60" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

